import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MessageCircle, X, Maximize2, Minimize2, Send, CheckCheck, Loader2, Star } from "lucide-react";
import {
    fetchSupportStats,
    fetchAdminConversations,
    fetchAdminMessages,
    pollAdminConversation,
    sendAdminMessage,
    closeConversationByAdmin,
} from "../service/api/orders";
import "../css/SupportChat.css";

const STATS_POLL_MS = 10000;
const LIST_POLL_MS = 5000;
const MESSAGE_POLL_MS = 5000;
const BUBBLE_SIZE = 52;
const BUBBLE_POS_KEY = "support_chat_bubble_pos";
const DRAG_THRESHOLD = 4;
const SCROLL_LOAD_THRESHOLD = 60;

function clampToViewport(pos) {
    const maxLeft = Math.max(window.innerWidth - BUBBLE_SIZE - 4, 4);
    const maxTop = Math.max(window.innerHeight - BUBBLE_SIZE - 4, 4);
    return {
        left: Math.min(Math.max(pos.left, 4), maxLeft),
        top: Math.min(Math.max(pos.top, 4), maxTop),
    };
}

function formatConvDate(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return "";
    const now = new Date();
    const sameYear = d.getFullYear() === now.getFullYear();
    return d.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        ...(sameYear ? {} : { year: "numeric" }),
    });
}

function formatConvDateTime(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return "";
    const datePart = formatConvDate(dateStr);
    const timePart = d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
    return `${datePart}, ${timePart}`;
}

// Splits OPEN conversations into "Today" (touched today) vs "Past Due"
// (last activity was before today — i.e. stale/overdue). Falls back to
// created_at if last_message_at isn't present.
function splitByDueness(list) {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const MIN_SANE_YEAR = 2000; // guards against null-date artifacts like "-000001-11-29..."

    const today = [];
    const pastDue = [];

    for (const c of list) {
        const lastMsgTime = c.last_message_at ? new Date(c.last_message_at) : null;
        const isSaneLastMsg = lastMsgTime && !Number.isNaN(lastMsgTime.getTime()) && lastMsgTime.getFullYear() >= MIN_SANE_YEAR;

        const refStr = isSaneLastMsg ? c.last_message_at : c.created_at;
        const ref = refStr ? new Date(refStr).getTime() : NaN;

        if (!Number.isNaN(ref) && ref >= startOfToday) {
            today.push(c);
        } else {
            pastDue.push(c);
        }
    }
    return { today, pastDue };
}

function sortConversations(list) {
    return [...list].sort((a, b) => {
        const aUnread = (a.unread_messages_count || 0) > 0 ? 1 : 0;
        const bUnread = (b.unread_messages_count || 0) > 0 ? 1 : 0;
        if (aUnread !== bUnread) return bUnread - aUnread; // unread (1) before read (0)

        const aTime = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
        const bTime = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
        return bTime - aTime; // most recent first
    });
}

const ORDER_STATUS_MAP = {
    1: "PLACED",
    2: "ACCEPTED",
    3: "ASSIGNED",
    4: "PICKUP",
    5: "DELIVERED",
    6: "CANCELLED",
    8: "AWAITING PAYMENT",
    9: "PAYMENT FAILED",
    10: "SLOT",
    11: "CONFIRMED",
};

export default function SupportChatWidget() {
    const [stats, setStats] = useState({ unread_messages: 0 });

    const [panelOpen, setPanelOpen] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const [conversations, setConversations] = useState([]);
    const [conversationsLoading, setConversationsLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    const [selectedId, setSelectedId] = useState(null);


    const [messages, setMessages] = useState([]);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);

    // Closing-ticket state (separate from "sending" so the input area and
    // the Close Ticket bar can show independent loading states).
    const [closingTicket, setClosingTicket] = useState(false);
    const [confirmingClose, setConfirmingClose] = useState(false);

    const [statusFilter, setStatusFilter] = useState("OPEN");

    const [bubblePos, setBubblePos] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const bubbleRef = useRef(null);
    const dragRef = useRef({ dragging: false, moved: false, offsetX: 0, offsetY: 0 });

    const lastSeenIdRef = useRef(0);
    const messagesEndRef = useRef(null);
    const sidebarListRef = useRef(null);
    const loadReqIdRef = useRef(0);
    const msgReqIdRef = useRef(0);
    const conversationsRef = useRef([]);



    useEffect(() => {
        conversationsRef.current = conversations;
    }, [conversations]);

    useEffect(() => {
        try {
            const saved = localStorage.getItem(BUBBLE_POS_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed && typeof parsed.top === "number" && typeof parsed.left === "number") {
                    setBubblePos(clampToViewport(parsed));
                }
            }
        } catch {}
    }, []);

    useEffect(() => {
        const onResize = () => setBubblePos((pos) => (pos ? clampToViewport(pos) : pos));
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const startDrag = (clientX, clientY) => {
        const el = bubbleRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        dragRef.current = { dragging: true, moved: false, offsetX: clientX - rect.left, offsetY: clientY - rect.top };
        setIsDragging(true);
    };

    const moveDrag = (clientX, clientY) => {
        const ds = dragRef.current;
        if (!ds.dragging) return;
        const rect = bubbleRef.current?.getBoundingClientRect();
        const dx = clientX - (ds.offsetX + (rect?.left ?? 0));
        const dy = clientY - (ds.offsetY + (rect?.top ?? 0));
        if (!ds.moved && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) ds.moved = true;
        setBubblePos(clampToViewport({ left: clientX - ds.offsetX, top: clientY - ds.offsetY }));
    };

    const endDrag = () => {
        const ds = dragRef.current;
        if (!ds.dragging) return;
        dragRef.current.dragging = false;
        setIsDragging(false);
        if (ds.moved) {
            setBubblePos((pos) => {
                if (pos) {
                    try { localStorage.setItem(BUBBLE_POS_KEY, JSON.stringify(pos)); } catch {}
                }
                return pos;
            });
        }
    };

    useEffect(() => {
        const onMouseMove = (e) => moveDrag(e.clientX, e.clientY);
        const onMouseUp = () => endDrag();
        const onTouchMove = (e) => {
            if (!dragRef.current.dragging) return;
            const t = e.touches[0];
            if (t) { e.preventDefault(); moveDrag(t.clientX, t.clientY); }
        };
        const onTouchEnd = () => endDrag();

        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
        window.addEventListener("touchmove", onTouchMove, { passive: false });
        window.addEventListener("touchend", onTouchEnd);
        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
            window.removeEventListener("touchmove", onTouchMove);
            window.removeEventListener("touchend", onTouchEnd);
        };
    }, []);

    const handleBubbleClick = () => {
        if (dragRef.current.moved) { dragRef.current.moved = false; return; }
        setPanelOpen((o) => !o);
    };

    const loadStats = useCallback(() => {
        fetchSupportStats()
            .then((res) => res?.success && setStats(res.data))
            .catch(() => {});
    }, []);

    useEffect(() => {
        loadStats();
        const interval = setInterval(loadStats, STATS_POLL_MS);
        return () => clearInterval(interval);
    }, [loadStats]);

    const loadConversations = useCallback(() => {
        const reqId = ++loadReqIdRef.current;
        setConversationsLoading(true);
        fetchAdminConversations(statusFilter, 1)
            .then((res) => {
                if (reqId !== loadReqIdRef.current) return;
                if (res?.success) {
                    const paginator = res.data;
                    setConversations(sortConversations(paginator?.data ?? []));
                    setCurrentPage(paginator?.current_page ?? 1);
                    setHasMore((paginator?.current_page ?? 1) < (paginator?.last_page ?? 1));
                }
            })
            .catch(() => {})
            .finally(() => {
                if (reqId === loadReqIdRef.current) setConversationsLoading(false);
            });
    }, [statusFilter]);

    const loadMoreConversations = useCallback(() => {
        if (loadingMore || !hasMore || conversationsLoading) return;
        const reqId = loadReqIdRef.current;
        const nextPage = currentPage + 1;
        setLoadingMore(true);
        fetchAdminConversations(statusFilter, nextPage)
            .then((res) => {
                if (reqId !== loadReqIdRef.current) return;
                if (res?.success) {
                    const paginator = res.data;
                    const newItems = paginator?.data ?? [];
                    setConversations((prev) => {
                        const existingIds = new Set(prev.map((c) => c.id));
                        const merged = [...prev, ...newItems.filter((c) => !existingIds.has(c.id))];
                        return sortConversations(merged);
                    });
                    setCurrentPage(paginator?.current_page ?? nextPage);
                    setHasMore((paginator?.current_page ?? nextPage) < (paginator?.last_page ?? nextPage));
                }
            })
            .catch(() => {})
            .finally(() => setLoadingMore(false));
    }, [statusFilter, currentPage, hasMore, loadingMore, conversationsLoading]);

    const handleSidebarScroll = () => {
        const el = sidebarListRef.current;
        if (!el) return;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (distanceFromBottom < SCROLL_LOAD_THRESHOLD) loadMoreConversations();
    };

    const refreshConversationsSilently = useCallback(() => {
        fetchAdminConversations(statusFilter, 1)
            .then((res) => {
                if (!res?.success) return;
                const fresh = res.data?.data ?? [];
                const current = conversationsRef.current;
                const currentIds = new Set(current.map((c) => c.id));

                const updated = current.map((c) => {
                    const match = fresh.find((f) => f.id === c.id);
                    return match ? { ...c, ...match } : c;
                });
                const brandNew = fresh.filter((f) => !currentIds.has(f.id));

                setConversations(sortConversations([...brandNew, ...updated]));
            })
            .catch(() => {});
    }, [statusFilter]);

    useEffect(() => {
        if (!panelOpen) return;
        loadConversations();
        const interval = setInterval(refreshConversationsSilently, LIST_POLL_MS);
        return () => clearInterval(interval);
    }, [panelOpen, statusFilter, loadConversations, refreshConversationsSilently]);

    const handleTabChange = (status) => {
        if (status === statusFilter) return;
        loadReqIdRef.current++;
        setConversations([]);
        setConversationsLoading(true);
        setCurrentPage(1);
        setHasMore(true);
        setStatusFilter(status);
        setSelectedId(null);
        setMessages([]);
    };

    const openConversation = useCallback((conversationId) => {
        if (conversationId === selectedId) return;

        const reqId = ++msgReqIdRef.current;
        setSelectedId(conversationId);
        setMessages([]);
        lastSeenIdRef.current = 0;
        setMessagesLoading(true);
        setConfirmingClose(false);

        fetchAdminMessages(conversationId)
            .then((res) => {
                if (reqId !== msgReqIdRef.current) return;
                if (res?.success) {
                    const msgs = res.data?.messages?.data ?? [];
                    setMessages(msgs);
                    if (msgs.length) lastSeenIdRef.current = msgs[msgs.length - 1].id;
                }
            })
            .catch(() => {})
            .finally(() => {
                if (reqId === msgReqIdRef.current) setMessagesLoading(false);
            });
    }, [selectedId]);

    useEffect(() => {
        if (!panelOpen || !selectedId) return;

        const tick = () => {
            pollAdminConversation(selectedId, lastSeenIdRef.current)
                .then((res) => {
                    if (!res?.success || !res.data?.has_new) return;

                    const incoming = res.data.messages || [];
                    if (incoming.length) {
                        setMessages((prev) => {
                            const existingIds = new Set(prev.map((m) => m.id));
                            return [...prev, ...incoming.filter((m) => !existingIds.has(m.id))];
                        });
                        lastSeenIdRef.current = res.data.last_message_id;
                    }

                    if (res.data.may_be_truncated) {
                        fetchAdminMessages(selectedId)
                            .then((full) => {
                                if (full?.success) {
                                    const msgs = full.data?.messages?.data ?? [];
                                    setMessages(msgs);
                                    if (msgs.length) lastSeenIdRef.current = msgs[msgs.length - 1].id;
                                }
                            })
                            .catch(() => {});
                    }

                    refreshConversationsSilently();
                    loadStats();
                })
                .catch(() => {});
        };

        const interval = setInterval(tick, MESSAGE_POLL_MS);
        return () => clearInterval(interval);
    }, [panelOpen, selectedId, refreshConversationsSilently, loadStats]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleSend = () => {
        const text = draft.trim();
        if (!text || !selectedId || sending) return;

        setSending(true);
        setDraft("");

        sendAdminMessage(selectedId, text)
            .then((res) => {
                if (res?.success && res.data) {
                    setMessages((prev) => [...prev, res.data]);
                    lastSeenIdRef.current = res.data.id;
                    refreshConversationsSilently();
                }
            })
            .catch(() => {})
            .finally(() => setSending(false));
    };

    const handleRequestCloseConversation = () => {
        if (!selectedId || closingTicket) return;
        setConfirmingClose(true);
    };


    const handleConfirmCloseConversation = () => {
        if (!selectedId || closingTicket) return;
        setClosingTicket(true);

        closeConversationByAdmin(selectedId)
            .then((res) => {
                if (res?.success === false) return;

                setConversations((prev) => prev.filter((c) => c.id !== selectedId));
                setSelectedId(null);
                setMessages([]);
                loadStats();
            })
            .catch(() => {})
            .finally(() => {
                setClosingTicket(false);
                setConfirmingClose(false);
            });
    };

    const handleCancelCloseConversation = () => setConfirmingClose(false);

    const selectedConversation = conversations.find((c) => c.id === selectedId);
    const unreadCount = stats?.unread_messages || 0;

    // Group OPEN tickets into Today / Past Due for the sidebar.
    const { today: todayConversations, pastDue: pastDueConversations } = useMemo(
        () => (statusFilter === "OPEN" ? splitByDueness(conversations) : { today: [], pastDue: [] }),
        [conversations, statusFilter]
    );

    const bubbleStyle = bubblePos
        ? { top: bubblePos.top, left: bubblePos.left, bottom: "auto", right: "auto" }
        : undefined;

    const renderConvItem = (conv) => (
        <div
            key={conv.id}
            className={`support-conv-item ${selectedId === conv.id ? "active" : ""} ${
                conv.status === "CLOSED" ? "closed" : ""
            } ${conv.unread_messages_count > 0 ? "has-unread" : ""}`}
            onClick={() => openConversation(conv.id)}
        >
            <div className="support-conv-item-main">
                <div className="support-conv-item-title-row">
                    <div className="support-conv-item-title">{conv.topic}</div>
                    <div className="support-conv-item-date">
                        {formatConvDate(conv.created_at)}
                    </div>
                </div>
                <div className="support-conv-item-user-row">
                    <span className="support-conv-item-name">{conv.user?.name}</span>
                    {conv.user?.phone && (
                        <span className="support-conv-item-phone">{conv.user.phone}</span>
                    )}
                </div>
                <div className="support-conv-item-preview">
                    {conv.last_message || "No messages yet"}
                </div>
            </div>
            {conv.status !== "CLOSED" && conv.unread_messages_count > 0 && (
                <span className="support-conv-item-badge">
                {conv.unread_messages_count}
            </span>
            )}

            {conv.status === "CLOSED" && conv.rating && (
                <div className="support-conv-item-rating">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                            key={i}
                            size={11}
                            fill={i < conv.rating ? "#f59e0b" : "none"}
                            color={i < conv.rating ? "#f59e0b" : "#cbd5e1"}
                        />
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <>
            <button
                ref={bubbleRef}
                className={`support-chat-bubble ${isDragging ? "dragging" : ""}`}
                style={bubbleStyle}
                onMouseDown={(e) => startDrag(e.clientX, e.clientY)}
                onTouchStart={(e) => { const t = e.touches[0]; if (t) startDrag(t.clientX, t.clientY); }}
                onClick={handleBubbleClick}
                title="Support conversations (drag to move)"
            >
                <MessageCircle size={22} />
                {stats?.open_conversations > 0 && (
                    <span className="support-chat-bubble-badge support-chat-bubble-badge--open">
                        {stats.open_conversations > 99 ? "99+" : stats.open_conversations}
                    </span>
                )}
                {unreadCount > 0 && (
                    <span className="support-chat-bubble-badge support-chat-bubble-badge--unread">
                        {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                )}
            </button>

            {panelOpen && (
                <>
                    {expanded && <div className="support-chat-overlay" onClick={() => setExpanded(false)} />}

                    <div className={`support-chat-panel ${expanded ? "expanded" : ""}`}>
                        <div className="support-chat-panel-topbar">
                            <button
                                className="support-chat-icon-btn"
                                onClick={() => setExpanded((e) => !e)}
                                title={expanded ? "Collapse" : "Expand"}
                            >
                                {expanded ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
                            </button>
                            <span className="support-chat-panel-title">Support</span>
                            <button
                                className="support-chat-icon-btn support-chat-icon-btn--close"
                                onClick={() => setPanelOpen(false)}
                                title="Close"
                            >
                                <X size={16} />
                            </button>
                        </div>

                        <div className="support-chat-body">
                            {/* Left: conversation list */}
                            <div className="support-chat-sidebar">
                                <div className="support-chat-sidebar-header">
                                    <span>Conversations</span>
                                    <span className="support-chat-sidebar-count">
                                        {conversationsLoading ? "…" : stats.open_conversations > 99 ? "99+" : stats.open_conversations}
                                    </span>
                                </div>

                                <div className="support-chat-tabs">
                                    <div
                                        className="support-chat-tabs-indicator"
                                        style={{ transform: statusFilter === "OPEN" ? "translateX(0%)" : "translateX(100%)" }}
                                    />
                                    <button
                                        className={`support-chat-tab ${statusFilter === "OPEN" ? "active" : ""}`}
                                        onClick={() => handleTabChange("OPEN")}
                                    >
                                        <span className="support-chat-tab-dot open" />
                                        Open
                                    </button>
                                    <button
                                        className={`support-chat-tab ${statusFilter === "CLOSED" ? "active" : ""}`}
                                        onClick={() => handleTabChange("CLOSED")}
                                    >
                                        <span className="support-chat-tab-dot closed" />
                                        Closed
                                    </button>
                                </div>

                                <div
                                    ref={sidebarListRef}
                                    className={`support-chat-sidebar-list ${conversationsLoading ? "is-refreshing" : ""}`}
                                    onScroll={handleSidebarScroll}
                                >
                                    {conversationsLoading && conversations.length === 0 &&
                                        Array.from({ length: 4 }).map((_, i) => (
                                            <div key={i} className="support-conv-item skeleton fade-pulse">
                                                <div className="skeleton-line w-70" />
                                                <div className="skeleton-line w-40" style={{ marginTop: 6 }} />
                                            </div>
                                        ))}

                                    {!conversationsLoading && conversations.length === 0 && (
                                        <div className="support-chat-empty">
                                            {statusFilter === "CLOSED" ? "No closed conversations" : "No conversations"}
                                        </div>
                                    )}

                                    {!conversationsLoading && conversations.length > 0 && (
                                        statusFilter === "OPEN" ? (
                                            <>
                                                {todayConversations.length > 0 && (
                                                    <div className="support-chat-section-header">
                                                        <span>Today</span>
                                                        <span className="support-chat-section-count">{todayConversations.length}</span>
                                                    </div>
                                                )}
                                                {todayConversations.map(renderConvItem)}

                                                {pastDueConversations.length > 0 && (
                                                    <div className="support-chat-section-header support-chat-section-header--overdue">
                                                        <span>Past Due</span>
                                                        <span className="support-chat-section-count">{pastDueConversations.length}</span>
                                                    </div>
                                                )}
                                                {pastDueConversations.map(renderConvItem)}
                                            </>
                                        ) : (
                                            conversations.map(renderConvItem)
                                        )
                                    )}

                                    {loadingMore && (
                                        <div className="support-conv-item-loading-more">
                                            <span className="support-spinner support-spinner--sm" />
                                        </div>
                                    )}

                                    {!hasMore && !conversationsLoading && conversations.length > 0 && (
                                        <div className="support-chat-list-end">You're all caught up</div>
                                    )}
                                </div>
                            </div>

                            {/* Right: chat */}
                            <div className={`support-chat-main ${selectedConversation?.status === "CLOSED" ? "is-closed" : ""}`}>
                                {!selectedId && (
                                    <div className="support-chat-placeholder">Select a conversation to view messages</div>
                                )}

                                {selectedId && (
                                    <>
                                        <div className="support-chat-main-header">
                                            <div>
                                                <div className="support-chat-main-title">
                                                    {selectedConversation?.user?.name || `Conversation #${selectedId}`}
                                                </div>
                                                {selectedConversation?.user?.phone && (
                                                    <a
                                                    href = {`tel:${selectedConversation.user.phone}`}
                                                    className="support-chat-main-phone"
                                                    >
                                                {selectedConversation.user.phone}
                                                    </a>
                                                    )}
                                                {selectedConversation?.created_at && (
                                                    <div className="support-chat-main-subtitle">
                                                        Started {formatConvDateTime(selectedConversation.created_at)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {selectedConversation?.order && (
                                        <div className="support-chat-order-card">
                                            <div
                                                className="support-chat-order-card-row support-chat-order-card-row--top">
                                                <div className="support-chat-order-card-id">
                                                    <span className="support-chat-order-card-label">Order</span>
                                                    <span className="support-chat-order-card-number">
                                                            #{selectedConversation.order.unique_order_id}
                                                        </span>
                                                    </div>

                                                    {(() => {
                                                        const statusLabel =
                                                            selectedConversation.order.status ||
                                                            ORDER_STATUS_MAP[selectedConversation.order.orderstatus_id] ||
                                                            (selectedConversation.order.orderstatus_id != null
                                                                ? `Status ${selectedConversation.order.orderstatus_id}`
                                                                : null);
                                                        if (!statusLabel) return null;
                                                        return (
                                                            <span className="support-chat-order-status" data-status={statusLabel.toUpperCase()}>
                                                                <span className="support-chat-order-status-dot" />
                                                                {statusLabel}
                                                            </span>
                                                        );
                                                    })()}
                                                </div>

                                                <div className="support-chat-order-card-row support-chat-order-card-row--bottom">
                                                    <div className="support-chat-order-card-amount-block">
                                                        <span className="support-chat-order-card-label">Amount</span>
                                                        <span className="support-chat-order-card-amount">
                                                            ₹{selectedConversation.order.total_amount ?? selectedConversation.order.total}
                                                        </span>
                                                    </div>
                                                    <a
                                                        href={`/order/${selectedConversation.order.unique_order_id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="support-chat-order-card-link"
                                                    >
                                                    View order details →
                                                </a>
                                            </div>
                                            </div>
                                            )}

                                        {selectedConversation?.status === "CLOSED" && (
                                            <div className="support-chat-closed-banner">
                                                <CheckCheck size={13} />
                                                This conversation is closed
                                            </div>
                                        )}

                                        {selectedConversation?.status === "CLOSED" && selectedConversation?.rating && (
                                            <div className="support-chat-review-card">
                                                <div className="support-chat-review-stars">
                                                    {Array.from({ length: 5 }).map((_, i) => (
                                                        <Star
                                                            key={i}
                                                            size={14}
                                                            fill={i < selectedConversation.rating ? "#f59e0b" : "none"}
                                                            color={i < selectedConversation.rating ? "#f59e0b" : "#cbd5e1"}
                                                        />
                                                    ))}
                                                    <span className="support-chat-review-rating-num">{selectedConversation.rating}/5</span>
                                                </div>
                                                {selectedConversation.review && (
                                                    <div className="support-chat-review-text">"{selectedConversation.review}"</div>
                                                )}
                                            </div>
                                        )}

                                        {messagesLoading && messages.length === 0 ? (
                                            <div className="support-chat-loading-state">
                                                <span className="support-spinner" />
                                                <span>Loading messages…</span>
                                            </div>
                                        ) : messages.length === 0 ? (
                                            <div className="support-chat-empty-messages">
                                                <MessageCircle size={26} />
                                                <span>No messages in this conversation yet</span>
                                            </div>
                                        ) : (
                                            <div className="support-chat-messages" key={selectedId}>
                                                {messages.map((m) => (
                                                    <div
                                                        key={m.id}
                                                        className={`support-msg-bubble ${m.sender_type === "ADMIN" ? "admin" : "customer"}`}
                                                    >
                                                        <div className="support-msg-text">{m.message}</div>
                                                        <div className="support-msg-time">
                                                            {m.created_at
                                                                ? new Date(m.created_at).toLocaleTimeString("en-IN", {
                                                                    hour: "numeric",
                                                                    minute: "2-digit",
                                                                    hour12: true,
                                                                })
                                                                : ""}
                                                        </div>
                                                    </div>
                                                ))}
                                                <div ref={messagesEndRef} />
                                            </div>
                                        )}


                                        {/* Floating "Close Ticket" bar — separate from ending the chat window,
                                            this closes the underlying support ticket. */}
                                        {selectedConversation?.status !== "CLOSED" && (
                                            <div className="support-chat-close-ticket-bar">
                                                {!confirmingClose ? (
                                                    <button
                                                        className="support-chat-close-ticket-btn"
                                                        onClick={handleRequestCloseConversation}
                                                        disabled={closingTicket}
                                                    >
                                                        <CheckCheck size={15} />
                                                        Close Ticket
                                                    </button>
                                                ) : (
                                                    <div className="support-chat-close-confirm">
                                                        <span className="support-chat-close-confirm-text">
                                                            Close this ticket? This can't be undone.
                                                        </span>
                                                        <div className="support-chat-close-confirm-actions">
                                                            <button
                                                                className="support-chat-close-cancel-btn"
                                                                onClick={handleCancelCloseConversation}
                                                                disabled={closingTicket}
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button
                                                                className="support-chat-close-confirm-btn"
                                                                onClick={handleConfirmCloseConversation}
                                                                disabled={closingTicket}
                                                            >
                                                                {closingTicket ? (
                                                                    <>
                                                                        <Loader2 size={14} className="support-chat-spin-icon" />
                                                                        Closing…
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <CheckCheck size={14} />
                                                                        Yes, close ticket
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <div className="support-chat-input-row">
                                            <input
                                                type="text"
                                                value={draft}
                                                onChange={(e) => setDraft(e.target.value)}
                                                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                                                placeholder={
                                                    selectedConversation?.status === "CLOSED" ? "Conversation closed" : "Type a reply…"
                                                }
                                                disabled={selectedConversation?.status === "CLOSED"}
                                            />
                                            <button
                                                onClick={handleSend}
                                                disabled={sending || !draft.trim() || selectedConversation?.status === "CLOSED"}
                                            >
                                                <Send size={16} />
                                            </button>
                                        </div>
                                    </>
                                    )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}